import { ReactNode } from "react";

import errorImages from "common/constants/errorImages";
import { FetchError } from "common/fetch/types";
import { NeverValues, Optional } from "common/utils/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";

import {
  DosageReminder,
  DosageReminderItem,
  DosageReminderLookup,
  DosageReminderPatient,
} from "../types";
import {
  useDosageReminderPatientItemQuery,
  useDosageReminderQuery,
  useDosageRemindersQuery,
} from "../hooks";
import { getReminderByPatientItem, getReminderKey } from "../utils";
import ReminderCreateForm from "./ReminderCreateForm";
import ReminderEditForm from "./ReminderEditForm";

/** Edits an already-loaded reminder */
type ReminderDrawerEditProps = {
  reminder: DosageReminder;
  canGoBack?: boolean;
};

/** Fetches the reminder to edit by id, or by item and patient, creating one when that pair has no reminder */
type ReminderDrawerLookupProps = {
  lookup: DosageReminderLookup;
};

/** Creates a new reminder for the item and patient */
type ReminderDrawerCreateProps = {
  patient: DosageReminderPatient;
  item: DosageReminderItem;
};

export type ReminderDrawerProps =
  | (ReminderDrawerEditProps & NeverValues<ReminderDrawerLookupProps & ReminderDrawerCreateProps>)
  | (ReminderDrawerLookupProps & NeverValues<ReminderDrawerEditProps & ReminderDrawerCreateProps>)
  | (ReminderDrawerCreateProps & NeverValues<ReminderDrawerEditProps & ReminderDrawerLookupProps>);

type ReminderQueryState = {
  reminder: Optional<DosageReminder>;
  isLoading: boolean;
  error: Optional<FetchError>;
  /** Renders the create drawer when the query finds no reminder, which otherwise shows not found */
  renderCreateReminder: (() => ReactNode) | null;
};

const renderReminderDrawer = ({
  reminder,
  isLoading,
  error,
  renderCreateReminder,
}: ReminderQueryState) => {
  if (isLoading) {
    return <DrawerLayout.Loading />;
  }
  if (error) {
    return <DrawerLayout.FetchError entityName="dosage reminder" error={error} />;
  }

  if (!reminder) {
    if (renderCreateReminder) {
      return renderCreateReminder();
    }
    return <DrawerLayout.Error title="Dosage reminder not found!" image={errorImages.Error404} />;
  }

  return <ReminderEditForm reminder={reminder} canGoBack={false} />;
};

type ReminderByIdDrawerProps = {
  id: number;
};

const ReminderByIdDrawer = ({ id }: ReminderByIdDrawerProps) => {
  const { data, isLoading, error } = useDosageReminderQuery(id);
  // A deleted reminder can't be edited, so it's shown as not found
  const reminder = data?.is_deleted ? undefined : data;
  return renderReminderDrawer({ reminder, isLoading, error, renderCreateReminder: null });
};

type ReminderByItemDrawerProps = {
  itemId: number;
  patientId: number;
};

type ReminderCreateByItemDrawerProps = {
  itemId: number;
  patientId: number;
};

/** Creates a reminder for an item and patient pair */
const ReminderCreateByItemDrawer = ({ itemId, patientId }: ReminderCreateByItemDrawerProps) => {
  const { data, isLoading, error } = useDosageReminderPatientItemQuery({
    patient_id: patientId,
    item_id: itemId,
  });

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }
  if (error || !data) {
    return <DrawerLayout.FetchError entityName="medication" error={error} />;
  }

  return <ReminderCreateForm patient={data.patient} item={data.item} />;
};

const ReminderByItemDrawer = ({ itemId, patientId }: ReminderByItemDrawerProps) => {
  const { data, isLoading, error } = useDosageRemindersQuery({
    item_id: itemId,
    patient_id: patientId,
  });

  // An item and patient can only have one active reminder, but may have several inactive ones
  const reminder =
    data && getReminderByPatientItem(data).get(getReminderKey({ patientId, itemId }));
  return renderReminderDrawer({
    reminder,
    isLoading,
    error,
    renderCreateReminder: () => (
      <ReminderCreateByItemDrawer itemId={itemId} patientId={patientId} />
    ),
  });
};

const ReminderDrawer = ({
  reminder,
  canGoBack = false,
  lookup,
  patient,
  item,
}: ReminderDrawerProps) => {
  if (reminder) {
    return <ReminderEditForm reminder={reminder} canGoBack={canGoBack} />;
  }

  if (patient && item) {
    return <ReminderCreateForm patient={patient} item={item} />;
  }

  if ("id" in lookup) {
    return <ReminderByIdDrawer id={lookup.id} />;
  }
  return <ReminderByItemDrawer itemId={lookup.item_id} patientId={lookup.patient_id} />;
};

export default ReminderDrawer;
